import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import MUIDataTable from "mui-datatables";
import axios from "axios";
import { toast } from "react-toastify";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Input } from "reactstrap";
import moment from "moment";

import { fetchEbtDrsConsumers, sendEbt, sendEbtDeregistration, sendEbtTermination } from "../../actions/ebt_drs_consumers.js";
import getQueryString from "./utils.js";

const parseXmlContent = (xmlString) => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, "application/xml");
    const getText = (tag) => {
      const el = doc.getElementsByTagName(tag)[0];
      return el ? el.textContent : "";
    };
    let elements = ["Document: " + doc.documentElement.nodeName];
    for (let i = 0; i < doc.documentElement.childNodes.length; i++) {
      const node = doc.documentElement.childNodes[i];
      if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.hasChildNodes() && node.childNodes.length > 1) {
          for (let j = 0; j < node.childNodes.length; j++) {
            const childNode = node.childNodes[j];
            if (childNode.nodeType === Node.ELEMENT_NODE) {
              elements.push(childNode.nodeName + ": " + childNode.textContent);
            }
          }
        } else {
          elements.push(node.nodeName + ": " + node.textContent);
        }
      }
    }
    return elements
  } catch (e) {
    return { raw: xmlString };
  }
};

const EBTDrsConsumers = (props) => {
  const dispatch = useDispatch();
  const store = useSelector((state) => state.ebtDrsConsumers);

  const [dataOptions, setDataOptions] = useState("");
  const [sortColumns, setSortColumns] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [logsData, setLogsData] = useState([]);
  const [currentRowId, setCurrentRowId] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [deregisterDatePickerOpen, setDeregisterDatePickerOpen] = useState(false);
  const [acknowledgeModalOpen, setAcknowledgeModalOpen] = useState(false);
  const [selectedConsumerId, setSelectedConsumerId] = useState(null);
  const [acknowledgeType, setAcknowledgeType] = useState("transaction");

  // Load data when component mounts
  useEffect(() => {
    dispatch(fetchEbtDrsConsumers(""));
  }, [dispatch]);

  // Refetch data when dataOptions changes (which happens on sort, page change, etc.)
  useEffect(() => {
    if (dataOptions !== "") {
      dispatch(fetchEbtDrsConsumers(dataOptions));
    }
  }, [dataOptions, dispatch]);

  const handleSendEbt = async (id) => {
    // Open date picker modal instead of sending directly
    setSelectedRowId(id);
    setDatePickerOpen(true);
  };

  const handleSendEbtWithDate = async (id, date, type) => {
    try {
      setLoadingId(id);
      await dispatch(sendEbt(id, date, type));
      toast.success("EBT registration sent successfully");
      // Refetch data to update the table
      dispatch(fetchEbtDrsConsumers(dataOptions));
      setDatePickerOpen(false);
    } catch (error) {
      toast.error("Failed to send EBT registration");
    } finally {
      setLoadingId(null);
    }
  };

  const handleSendEbtDeregistration = async (id) => {
    // Open date picker modal for deregistration
    setSelectedRowId(id);
    setDeregisterDatePickerOpen(true);
  };

  const handleSendEbtDeregistrationWithDate = async (id, date) => {
    try {
      setLoadingId(id);
      await dispatch(sendEbtDeregistration(id, date));
      toast.success("EBT deregistration sent successfully");
      // Refetch data to update the table
      dispatch(fetchEbtDrsConsumers(dataOptions));
      setDeregisterDatePickerOpen(false);
    } catch (error) {
      toast.error("Failed to send EBT deregistration");
    } finally {
      setLoadingId(null);
    }
  };

  const handleSendAck = async (id) => {
    try {
      setLoadingId(id);
      await axios.post(`/ebt_drs_consumers/${id}/acknowledge`, {
        acknowledgeType: acknowledgeType
      });
      toast.success("Acknowledgement sent successfully");
      // Refetch data to update the table
      dispatch(fetchEbtDrsConsumers(dataOptions));
    } catch (error) {
      toast.error("Failed to send acknowledgement");
    } finally {
      setLoadingId(null);
    }
  };

  const openAcknowledgeModal = (id) => {
    setSelectedConsumerId(id);
    setAcknowledgeType("transaction");
    setAcknowledgeModalOpen(true);
  };

  const closeAcknowledgeModal = () => {
    setAcknowledgeModalOpen(false);
    setSelectedConsumerId(null);
    setAcknowledgeType("transaction");
  };

  const handleTerminate = async (id) => {
    // Show confirmation dialog
    if (!window.confirm("Are you sure you want to terminate this EBT process?")) {
      return;
    }

    try {
      setLoadingId(id);
      await dispatch(sendEbtTermination(id, moment().format('YYYY-MM-DD')));
      toast.success("Termination notification sent successfully");
      // Refetch data to update the table
      dispatch(fetchEbtDrsConsumers(dataOptions));
    } catch (error) {
      toast.error("Failed to send termination notification");
    } finally {
      setLoadingId(null);
    }
  };

  const columns = [
    {
      label: "ID",
      name: "id",
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      label: "Name",
      name: "nickname",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "account_number",
      label: "Account Number",
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      label: "Load Facility",
      name: "load_facility",
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: "registration_status",
      label: "Registration Status",
      options: {
        filter: true,
        sort: true,
        filterType: "checkbox",
        filterOptions: {
          names: ["pending", "sent", "validated", "completed", "acknowledged"],
        },
        customBodyRender: (value, tableMeta, updateValue) => {
          if (!value) return "-";
          if (value == "acknowledged") {
            return "Complete acknowledged";
          }
          return value.charAt(0).toUpperCase() + value.slice(1);
        }
      },
    },
    {
      name: "ACTIONS",
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRenderLite: (dataIndex, rowIndex) => {
          const rowData = store.ebt_drs_consumers[dataIndex];
          const status = rowData.registration_status;
          
          // Determine which button to show based on status
          if (status === "pending") {
            return (
              <div>
                <Button
                  className="btn-rounded mb-2 me-2"
                  color="success"
                  size="sm"
                  onClick={() => handleSendEbt(rowData.id)}
                  disabled={loadingId === rowData.id}
                >
                  {loadingId === rowData.id ? (
                    <span className="d-flex align-items-center">
                      <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    </span>
                  ) : (
                    "Register"
                  )}
                </Button>
                <Button
                  className="btn-rounded mb-2 me-2 ml-2"
                  color="warning"
                  size="sm"
                  onClick={() => handleSendEbtDeregistration(rowData.id)}
                  disabled={loadingId === rowData.id}
                >
                  {loadingId === rowData.id ? (
                    <span className="d-flex align-items-center">
                      <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    </span>
                  ) : (
                    "Deregister"
                  )}
                </Button>
              </div>
            );
          } else if (status === "completed") {
            return (
              <div>
                <Button
                  className="btn-rounded mb-2 me-2"
                  color="success"
                  size="sm"
                  onClick={() => openAcknowledgeModal(rowData.id)}
                  disabled={loadingId === rowData.id}
                >
                  {loadingId === rowData.id ? (
                    <span className="d-flex align-items-center">
                      <span className="spinner-border spinner-border-sm mr-2 text-white" role="status" aria-hidden="true"></span>
                    </span>
                  ) : (
                    "Send Acknowledgement"
                  )}
                </Button>
              </div>
            );
          }
          
          // For all other statuses, show nothing
          return null;
        },
      },
    },
  ];

  const fetchLogs = async (id) => {
    try {
      const response = await axios.get(`/ebt_drs_consumers/${id}/logs`);
      const data = response.data.map((data, index) => {
        data.content = parseXmlContent(data.content);
        return data
      });
      setLogsData(data);
      setCurrentRowId(id);
      setLogsDialogOpen(true);
    } catch (error) {
      toast.error("Failed to fetch logs");
    }
  };

  const options = {
    filter: false,
    search: false,
    download: false,
    print: false,
    viewColumns: false,
    count: store.total_ebt_drs_consumers || 0,
    serverSide: true,
    onTableChange: (action, tableState) => {
      switch (action) {
        case "changePage":
          setDataOptions(getQueryString(tableState, sortColumns));
          break;
        case "sort":
          if (
            sortColumns.filter(
              (sort) => sort.name === tableState.sortOrder.name
            ).length > 0
          ) {
            // update the existing sortColumn
            let index = sortColumns.findIndex(
              (sort) => sort.name === tableState.sortOrder.name
            );
            sortColumns[index] = {
              name: tableState.sortOrder.name,
              direction: tableState.sortOrder.direction,
            };

            setSortColumns(sortColumns);
          } else {
            // add a new sortColumn
            sortColumns.push({
              name: tableState.sortOrder.name,
              direction: tableState.sortOrder.direction,
            });

            setSortColumns(sortColumns);
          }

          setDataOptions(getQueryString(tableState, sortColumns));
          break;
        case "changeRowsPerPage":
          setDataOptions(getQueryString(tableState, sortColumns));
          break;
      }
    },
  };

  // Add Logs button to the actions column
  const updatedColumns = [...columns];
  updatedColumns[5].options.customBodyRenderLite = (dataIndex, rowIndex) => {
    const rowData = store.ebt_drs_consumers[dataIndex];
    const status = rowData.registration_status;
    
          // Determine which button to show based on status
          if (status === "pending") {
            return (
              <div>
                <Button
                  className="btn-rounded mb-2 me-2 ml-2"
                  color="info"
                  size="sm"
                  onClick={() => fetchLogs(rowData.id)}
                >
                  Logs
                </Button>
                <Button
                  className="btn-rounded mb-2 me-2"
                  color="success"
                  size="sm"
                  onClick={() => handleSendEbt(rowData.id)}
                  disabled={loadingId === rowData.id}
                >
                  {loadingId === rowData.id ? (
                    <span className="d-flex align-items-center">
                      <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    </span>
                  ) : (
                    "Register"
                  )}
                </Button>
                <Button
                  className="btn-rounded mb-2 me-2 ml-2"
                  color="warning"
                  size="sm"
                  onClick={() => handleSendEbtDeregistration(rowData.id)}
                  disabled={loadingId === rowData.id}
                >
                  {loadingId === rowData.id ? (
                    <span className="d-flex align-items-center">
                      <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    </span>
                  ) : (
                    "Deregister"
                  )}
                </Button>
              </div>
            );
          } else if (status === "validated") {
            return (
              <div>
                <Button
                  className="btn-rounded mb-2 me-2 ml-2"
                  color="info"
                  size="sm"
                  onClick={() => fetchLogs(rowData.id)}
                >
                  Logs
                </Button>
                <Button
                  className="btn-rounded mb-2 me-2"
                  color="danger"
                  size="sm"
                  onClick={() => handleTerminate(rowData.id)}
                  disabled={loadingId === rowData.id}
                >
                  {loadingId === rowData.id ? (
                    <span className="d-flex align-items-center">
                      <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    </span>
                  ) : (
                    "Terminate"
                  )}
                </Button>
              </div>
            );
          } else if (status === "completed") {
            return (
              <div>
                <Button
                  className="btn-rounded mb-2 me-2"
                  color="success"
                  size="sm"
                  onClick={() => openAcknowledgeModal(rowData.id)}
                  disabled={loadingId === rowData.id}
                >
                  {loadingId === rowData.id ? (
                    <span className="d-flex align-items-center">
                      <span className="spinner-border spinner-border-sm mr-2 text-white" role="status" aria-hidden="true"></span>
                    </span>
                  ) : (
                    "Send Ack"
                  )}
                </Button>
                <Button
                  className="btn-rounded mb-2 me-2 ml-2"
                  color="info"
                  size="sm"
                  onClick={() => fetchLogs(rowData.id)}
                >
                  Logs
                </Button>
              </div>
            );
          }
    
    // For all other statuses, show only Logs button
    return (
      <Button
        className="btn-rounded mb-2 me-2"
        color="info"
        size="sm"
        onClick={() => fetchLogs(rowData.id)}
      >
        Logs
      </Button>
    );
  };

  return (
    <div>
      <MUIDataTable
        data={store.ebt_drs_consumers || []}
        columns={updatedColumns}
        options={options}
      />
      
      {/* Logs Dialog */}
      {logsDialogOpen && (
        <div className="modal show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">EBT Logs</h5>
              </div>
              <div className="modal-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {logsData && logsData.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Time</th>
                          <th>Content</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logsData.map((log, index) => (
                          <tr key={index}>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              {log.type.charAt(0).toUpperCase() + log.type.slice(1).replaceAll("_", " ") || "-"}
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>{moment(log.createdAt).format("YYYY-MM-DD HH:mm:ss") || "-"}</td>
                            <td>
                              <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                                {log.content.map((line, idx) => (
                                  <div key={idx}>{line}</div>
                                ))}
                              </pre>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p>No logs found</p>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setLogsDialogOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Date Picker Modal */}
      {datePickerOpen && (
        <div className="modal show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">EBT Registration</h5>
              </div>
              <div className="modal-body">
                <p>Action Date</p>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={moment().format('YYYY-MM-DD')}
                />
                <div className="mt-3">
                  <label htmlFor="typeSelect" className="form-label">Request Type</label>
                  <Input
                    type="select"
                    id="typeSelect"
                    defaultValue=""
                    onChange={(e) => setSelectedType(e.target.value)}
                  >
                    <option value="">Select Type</option>
                    <option value="REG">Registration</option>
                    <option value="DTF">DRA Transfer</option>
                    <option value="LTF">Facility Transfer</option>
                  </Input>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setDatePickerOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleSendEbtWithDate(selectedRowId, selectedDate, selectedType)}
                  disabled={!selectedDate || !selectedType}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deregistration Date Picker Modal */}
      {deregisterDatePickerOpen && (
        <div className="modal show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">EBT Deregistration</h5>
              </div>
              <div className="modal-body">
                <p>Action Date</p>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={moment().format('YYYY-MM-DD')}
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setDeregisterDatePickerOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleSendEbtDeregistrationWithDate(selectedRowId, selectedDate)}
                  disabled={!selectedDate}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Acknowledge Modal */}
      {acknowledgeModalOpen && (
        <div className="modal show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Send Acknowledgement</h5>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="acknowledgeType" className="form-label">Type</label>
                  <select
                    id="acknowledgeType"
                    className="form-select"
                    value={acknowledgeType}
                    onChange={(e) => setAcknowledgeType(e.target.value)}
                  >
                    <option value="transaction">Transaction Acknowledgement</option>
                    <option value="termination_transaction">Termination Acknowledgement</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeAcknowledgeModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={async () => {
                    try {
                      setLoadingId(selectedConsumerId);
                      
                      // Call backend API with selected acknowledge type
                      await axios.post(`/ebt_drs_consumers/${selectedConsumerId}/acknowledge`, {
                        acknowledgeType: acknowledgeType
                      });
                      
                      // Refetch data to update the table
                      dispatch(fetchEbtDrsConsumers(dataOptions));
                      toast.success("Acknowledgement sent successfully");
                      
                      // Close the modal
                      closeAcknowledgeModal();
                    } catch (error) {
                      toast.error("Failed to send acknowledgement: " + (error.response?.data?.message || error.message));
                    } finally {
                      setLoadingId(null);
                    }
                  }}
                  disabled={loadingId === selectedConsumerId}
                >
                  {loadingId === selectedConsumerId ? (
                    <span className="d-flex align-items-center">
                      <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    </span>
                  ) : (
                    "Send Ack"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EBTDrsConsumers;
